/**
 * Validation Engine for Import Note Feature
 * Validates import text against format rules and data integrity constraints
 */

/**
 * Error codes for validation failures
 * @enum {string}
 */
const ValidationErrorCode = {
  ODD_LINE_COUNT: 'ODD_LINE_COUNT',
  EMPTY_LINE: 'EMPTY_LINE',
  INVALID_FORMAT: 'INVALID_FORMAT'
};

/**
 * Error message templates for validation failures
 * Templates follow the exact format specified in Requirements 8.2 and 8.3
 */
const ErrorMessageTemplates = {
  /**
   * Template for incomplete card entry error
   */
  ODD_LINE_COUNT: "Import failed: Incomplete flashcard entry. Each card needs a question and answer, with an optional ';memo' line between them.",
  
  /**
   * Template for empty line error (Requirement 8.3)
   * @param {number} lineNumber - The line number where the empty line was detected
   * @returns {string} - Formatted error message
   */
  EMPTY_LINE: (lineNumber) => `Import failed: Empty line detected at line ${lineNumber}. All lines must contain content.`
};

function isTaggedFormat(lines) {
  return lines.some((line) => /^(@q:|@a:|q:|a:|r:|d:|@d:|@r:|@pre:|@front:|@after:|@back:|@word:|@tts:)/i.test(line.trim()));
}

function getContentLines(text) {
  return text.split('\n').filter((line) => line.trim() !== '');
}

function isBlockSeparator(line) {
  const trimmed = String(line || '').trim();
  if (/^-{3,}/.test(trimmed)) {
    return true;
  }
  if (/^[?-]+$/.test(trimmed) && trimmed.includes('-') && trimmed.length >= 2) {
    return true;
  }
  return /^[\p{Dash_Punctuation}-]+$/u.test(trimmed) &&
    (trimmed.length >= 3 || (trimmed.length >= 2 && /[\p{Dash_Punctuation}]/u.test(trimmed)));
}

function splitTaggedBlocks(text) {
  const blocks = [];
  let currentBlock = [];

  String(text || '').replace(/\r\n/g, '\n').split('\n').forEach((line) => {
    if (isBlockSeparator(line)) {
      if (currentBlock.length > 0) {
        blocks.push(currentBlock);
        currentBlock = [];
      }
      return;
    }
    currentBlock.push(line);
  });

  if (currentBlock.length > 0) {
    blocks.push(currentBlock);
  }

  return blocks.filter((block) => block.some((line) => line.trim() !== ''));
}

function parseReadingBlockTag(line) {
  const match = String(line || '').trim().match(/^@([AB]\s+name|[AB](?:\[once\])?|Scene|Story)\s*:\s*(.*)$/i);
  if (!match) return null;
  return {
    tag: match[1].replace(/\[once\]/i, '').replace(/\s+/g, ' ').toLowerCase(),
    ...(/\[once\]/i.test(match[1]) ? { practiceMode: 'once' } : {}),
    value: match[2].trim(),
  };
}

function validateMergedReadingBlock(title, lines = []) {
  if (!String(title || '').trim()) {
    return 'Import failed: @R: reading cards need a title.';
  }

  const contentLines = lines.map((line) => String(line || '').trim()).filter(Boolean);
  if (contentLines.length === 0) {
    return 'Import failed: @R: reading cards need @Story:, @A:, or @B: content.';
  }

  let activeTag = null;
  let activeHasText = false;
  let hasPractice = false;

  for (const line of contentLines) {
    if (/^(d|r|scene|story|[ab](?:\s+name)?)\s*:/i.test(line)) {
      return 'Import failed: Reading blocks must use @ tags such as @Story:, @Scene:, @A:, and @B:.';
    }

    const tagged = parseReadingBlockTag(line);
    if (tagged) {
      if (activeTag && !activeHasText) {
        return `Import failed: @${activeTag}: needs text.`;
      }

      activeTag = null;
      activeHasText = false;

      if (tagged.tag === 'a name' || tagged.tag === 'b name') {
        continue;
      }

      if (tagged.tag === 'scene') {
        if (!tagged.value) return 'Import failed: @Scene: needs text.';
        continue;
      }

      activeTag = tagged.tag.toUpperCase();
      activeHasText = Boolean(tagged.value);
      if (tagged.value) hasPractice = true;
      continue;
    }

    if (line.startsWith('@')) {
      return 'Import failed: Unsupported @R: block tag. Use @A name:, @B name:, @Scene:, @Story:, @A:, or @B:.';
    }

    if (!activeTag) {
      return 'Import failed: @R: continuation lines must follow @Story:, @Scene:, @A:, or @B:.';
    }

    activeHasText = true;
    hasPractice = true;
  }

  if (activeTag && !activeHasText) {
    return `Import failed: @${activeTag}: needs text.`;
  }

  if (!hasPractice) {
    return 'Import failed: @R: needs at least one practice segment: @Story:, @A:, or @B:.';
  }

  return null;
}

/**
 * @typedef {Object} ValidationError
 * @property {'ODD_LINE_COUNT' | 'EMPTY_LINE' | 'INVALID_FORMAT'} code - Machine-readable error code
 * @property {string} message - Human-readable error message
 * @property {number} [lineNumber] - Line number where error occurred (for empty line errors)
 */

/**
 * @typedef {Object} ValidationResult
 * @property {boolean} isValid - Whether the input passes all validation rules
 * @property {ValidationError} [error] - Details about validation failure (if any)
 * @property {number} [lineCount] - Total number of non-empty lines
 */

/**
 * @interface ValidationEngine
 * @description Interface for validation engine implementations
 */
/**
 * @typedef {Object} ValidationEngine
 * @property {function(string): ValidationResult} validate - Validates import text against all format rules
 */

/**
 * Validation Engine implementation
 * Enforces format rules: even line count, no empty lines
 */
class ImportValidator {
  /**
   * Validates import text against all format rules
   * @param {string} text - The text to validate
   * @returns {ValidationResult} - Validation result with error details if invalid
   */
  validate(text) {
    // Split text into importable content lines. Blank lines are ignored before parsing.
    const lines = getContentLines(text);

    if (lines.length === 0) {
      return {
        isValid: false,
        error: {
          code: ValidationErrorCode.INVALID_FORMAT,
          message: 'Import failed: No importable content found.'
        },
        lineCount: 0
      };
    }
    
    if (isTaggedFormat(String(text || '').split('\n'))) {
      const taggedError = this.validateTaggedText(text);
      if (taggedError) {
        return taggedError;
      }

      return {
        isValid: true,
        lineCount: lines.filter(line => line.trim() !== '').length
      };
    }

    // Plain cards may also use the same separators as tagged cards. Validate
    // each block independently so separator rows are never mistaken for Q/A.
    const plainBlocks = splitTaggedBlocks(text);
    for (const block of plainBlocks) {
      const lineCountError = this.checkEvenLineCount(block);
      if (lineCountError) {
        return {
          isValid: false,
          error: lineCountError,
          lineCount: lines.filter(line => line.trim() !== '').length
        };
      }
    }
    
    // All validation checks passed
    return {
      isValid: true,
      lineCount: lines.filter(line => line.trim() !== '').length
    };
  }

  /**
   * Checks for empty or whitespace-only lines
   * @private
   * @param {string[]} lines - Array of lines to check
   * @returns {ValidationError | null} - Error if empty line found, null otherwise
   */
  checkEmptyLines(lines) {
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      // Check if line is empty or contains only whitespace
      if (line.trim() === '') {
        return {
          code: ValidationErrorCode.EMPTY_LINE,
          message: ErrorMessageTemplates.EMPTY_LINE(i + 1),
          lineNumber: i + 1
        };
      }
    }
    return null;
  }

  /**
   * Checks that non-empty lines form complete entries:
   * - Question + Answer
   * - Question + ;memo + Answer
   * @private
   * @param {string[]} lines - Array of lines to check
   * @returns {ValidationError | null} - Error if line count is odd, null otherwise
   */
  checkEvenLineCount(lines) {
    const nonEmptyLines = lines.filter(line => line.trim() !== '');

    let index = 0;
    while (index < nonEmptyLines.length) {
      index += 1; // question

      if (index >= nonEmptyLines.length) {
        return {
          code: ValidationErrorCode.ODD_LINE_COUNT,
          message: ErrorMessageTemplates.ODD_LINE_COUNT
        };
      }

      if (nonEmptyLines[index].trim().startsWith(';')) {
        index += 1; // memo

        if (index >= nonEmptyLines.length) {
          return {
            code: ValidationErrorCode.ODD_LINE_COUNT,
            message: ErrorMessageTemplates.ODD_LINE_COUNT
          };
        }
      }

      index += 1; // answer
    }

    if (index !== nonEmptyLines.length) {
      return {
        code: ValidationErrorCode.ODD_LINE_COUNT,
        message: ErrorMessageTemplates.ODD_LINE_COUNT
      };
    }

    return null;
  }

  validateTaggedText(text) {
    const blocks = splitTaggedBlocks(text);
    const nonEmptyLineCount = getContentLines(text).length;
    const allowedTopLevelTags = new Set(['@q', '@a', '@r', '@pre', '@front', '@after', '@back', '@word', '@tts']);

    if (blocks.length === 0) {
      return {
        isValid: false,
        error: {
          code: ValidationErrorCode.INVALID_FORMAT,
          message: 'Import failed: Tagged format needs at least one card block.'
        },
        lineCount: nonEmptyLineCount
      };
    }

    for (const block of blocks) {
      if (!isTaggedFormat(block)) {
        const plainError = this.checkEvenLineCount(block);
        if (plainError) {
          return {
            isValid: false,
            error: plainError,
            lineCount: nonEmptyLineCount
          };
        }
        continue;
      }

      let hasQuestion = false;
      let hasAnswer = false;
      let hasReading = false;

      for (let index = 0; index < block.length; index += 1) {
        const line = block[index];
        const trimmed = line.trim();
        if (!trimmed) continue;

        const separatorIndex = trimmed.indexOf(':');
        if (separatorIndex < 0) {
          if (hasReading) continue;
          return {
            isValid: false,
            error: {
              code: ValidationErrorCode.INVALID_FORMAT,
              message: 'Import failed: Tagged format lines must use @Q:, @A:, @R:, @pre:, @front:, @back:, @after:, @tts:, or @word:.'
            },
            lineCount: nonEmptyLineCount
          };
        }

        const tag = trimmed.slice(0, separatorIndex).trim().toLowerCase();
        const value = trimmed.slice(separatorIndex + 1).trim();

        if (tag === 'q' || tag === 'a') {
          return {
            isValid: false,
            error: {
              code: ValidationErrorCode.INVALID_FORMAT,
              message: `Import failed: ${tag.toUpperCase()}: is legacy format. Use @${tag.toUpperCase()}: instead.`
            },
            lineCount: nonEmptyLineCount
          };
        }

        if (!allowedTopLevelTags.has(tag)) {
          if (hasReading) continue;
          return {
            isValid: false,
            error: {
              code: ValidationErrorCode.INVALID_FORMAT,
              message: `Import failed: Unsupported tag "${tag}". Use @Q:, @A:, @R:, @pre:, @front:, @back:, @after:, @tts:, or @word:.`
            },
            lineCount: nonEmptyLineCount
          };
        }

        if (tag === '@q') hasQuestion = true;
        if (tag === '@a') hasAnswer = true;
        if (tag === '@r') {
          hasReading = true;
          const errorMessage = validateMergedReadingBlock(value, block.slice(index + 1));
          if (errorMessage) {
            return {
              isValid: false,
              error: {
                code: ValidationErrorCode.INVALID_FORMAT,
                message: errorMessage
              },
              lineCount: nonEmptyLineCount
            };
          }
          break;
        }
      }

      if (hasReading && (hasQuestion || hasAnswer)) {
        return {
          isValid: false,
          error: {
            code: ValidationErrorCode.INVALID_FORMAT,
            message: 'Import failed: @R: card blocks cannot mix with @Q: or @A:.'
          },
          lineCount: nonEmptyLineCount
        };
      }

      if (!hasReading && (!hasQuestion || !hasAnswer)) {
        return {
          isValid: false,
          error: {
            code: ValidationErrorCode.INVALID_FORMAT,
            message: 'Import failed: Each tagged card block needs both @Q: and @A:.'
          },
          lineCount: nonEmptyLineCount
        };
      }
    }

    return null;
  }
}

export default ImportValidator;
export { ValidationErrorCode, ErrorMessageTemplates };
